import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import useVoyagerStore from '../stores/useVoyagerStore';
import { dismissCloseToast, undoClose, useCloseToast } from './closeUndo';
import { PHONE_QUERY, useChatSidebar } from './chatSidebarState';

const TEXT: Record<string, (title: string) => string> = {
  closing: (t) => `Closing “${t}”…`,
  closed: () => 'Closed. It’s in History.',
  reopening: (t) => `Reopening “${t}”…`,
  'close-failed': (t) => `Couldn’t close “${t}”. It’s still open.`,
  'reopen-failed': (t) => `Couldn’t reopen “${t}”. It’s still in History.`,
};

/**
 * The close-Undo toast (#765). The shell hosts it on every page, so it
 * survives navigation and the sidebar unmounting. While the phone drawer is
 * open its modal hides the page from assistive tech and traps focus, so the
 * toast is rendered INSIDE the drawer (`inDrawer`) and the page's copy steps
 * aside — one toast, always reachable. The drawer flag is cleared when the
 * drawer unmounts, so the page copy is never suppressed by a drawer that is
 * gone.
 */
export default function CloseUndoToast({ inDrawer = false }: { inDrawer?: boolean }) {
  const toast = useCloseToast((s) => s.toast);
  const generation = useVoyagerStore((s) => s.threadsGeneration);
  const phone = useMediaQuery(PHONE_QUERY);
  const drawerOpen = useChatSidebar((s) => s.drawerOpen);
  if (!toast || toast.generation !== generation) return null;
  if (!inDrawer && phone && drawerOpen) return null;
  const failed = toast.phase === 'close-failed' || toast.phase === 'reopen-failed';
  return <Box data-testid={inDrawer ? 'close-toast-drawer' : 'close-toast'}
    sx={inDrawer ? { px: 1.5, py: 1, borderTop: '1px solid var(--line)' }
      : { position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, maxWidth: 'calc(100vw - 32px)' }}>
    <Box role="status" data-phase={toast.phase} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'var(--ink)', color: 'var(--surface)', pl: 1.75, pr: 1, py: 1, borderRadius: '9px', fontSize: 13.5, boxShadow: inDrawer ? 'none' : 'var(--shadow)' }}>
      <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere', flex: 1 }}>{TEXT[toast.phase](toast.title)}</Box>
      {!failed && <Button size="small" disabled={toast.phase !== 'closed'} onClick={() => { void undoClose(); }}
        sx={{ color: 'var(--accent-hi)', fontWeight: 700, flexShrink: 0, '&.Mui-disabled': { color: 'var(--ink-3)' } }}>Undo</Button>}
      <Button size="small" aria-label="Dismiss" onClick={dismissCloseToast} sx={{ color: 'var(--surface)', minWidth: 0, flexShrink: 0 }}>✕</Button>
    </Box>
  </Box>;
}
